import { useState } from "react";
import DateSelect from "@/components/DateSelect";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import CopyButton from "@/components/CopyButton";
import { DateValueType } from "react-tailwindcss-datepicker";
import { calculateMonthsBetween } from "@/lib/dateHelpers";

interface InternshipHoursCalculatorDialogProps {
  open: boolean;
  onClose: () => void;
}

export default function InternshipHoursCalculatorDialog({ open, onClose }: InternshipHoursCalculatorDialogProps) {
  const [startDate, setStartDate] = useState<DateValueType | string>(null);
  const [endDate, setEndDate] = useState<DateValueType | string>(null);
  const [creditHours, setCreditHours] = useState<number | null>(null);
  const [startDateError, setStartDateError] = useState<string>("");
  const [endDateError, setEndDateError] = useState<string>("");

  const validateDates = () => {
    let isValid = true;
    setStartDateError("");
    setEndDateError("");

    if (!startDate || (typeof startDate === "object" && !startDate.startDate)) {
      setStartDateError("Start date is required");
      isValid = false;
    }

    if (!endDate || (typeof endDate === "object" && !endDate.startDate)) {
      setEndDateError("End date is required");
      isValid = false;
    }

    if (
      isValid &&
      typeof startDate === "object" &&
      typeof endDate === "object" &&
      startDate &&
      endDate &&
      startDate.startDate &&
      endDate.startDate
    ) {
      const start = new Date(startDate.startDate);
      const end = new Date(endDate.startDate);

      if (start >= end) {
        setStartDateError("Start date must be before end date");
        isValid = false;
      }
    }

    return isValid;
  };

  const handleCalculate = () => {
    if (validateDates()) {
      if (startDate && endDate && typeof startDate === "object" && typeof endDate === "object") {
        const startDateStr = startDate.startDate;
        const endDateStr = endDate.startDate;

        if (startDateStr && endDateStr) {
          const calculatedCreditHours = calculateMonthsBetween(startDateStr, endDateStr);
          setCreditHours(calculatedCreditHours);
        }
      }
    }
  };

  const handleStartDateChange = (date: DateValueType | string) => {
    setStartDate(date);
    if (startDateError) {
      setStartDateError("");
    }
  };

  const handleEndDateChange = (date: DateValueType | string) => {
    setEndDate(date);
    if (endDateError) {
      setEndDateError("");
    }
  };

  const handleCancel = () => {
    setStartDate(null);
    setEndDate(null);
    setCreditHours(null);
    setStartDateError("");
    setEndDateError("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClosed={handleCancel}
      headline="Internship Hours Calculator"
      actions={
        <div className="flex gap-2">
          <Button variant="text" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleCalculate} disabled={!startDate || !endDate}>
            Calculate
          </Button>
        </div>
      }
      className="w-[400px] max-w-[90vw]"
      displayOverflow
      dismissOnOutsideClick
    >
      <div className="mt-6">
        <input className="mt-4 ml-1 absolute" />

        <div className="space-y-6">
          <DateSelect
            key={`start-${open}`}
            label="Internship Start Date *"
            value={startDate}
            onChange={handleStartDateChange}
            useSingleDate
            showFooter={false}
            useModal={true}
            error={!!startDateError}
            errorMessage={startDateError}
          />
          <DateSelect
            key={`end-${open}`}
            label="Internship End Date *"
            value={endDate}
            onChange={handleEndDateChange}
            useSingleDate
            showFooter={false}
            useModal={true}
            error={!!endDateError}
            errorMessage={endDateError}
          />
          {creditHours !== null && (
            <div className="flex flex-col gap-1">
              <div className="text-sys-brand-on-surface-variant text-body-small">Calculated Credit Hours</div>
              <div className="flex items-center gap-2">
                <div className="text-sys-brand-on-surface text-body-large-semibold">{creditHours.toFixed(1)} hours</div>
                <CopyButton textToCopy={creditHours.toFixed(1)} copyFieldLabel="Credit Hours" />
              </div>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
}
